import React from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft, Download, Lock, CheckCircle2, Clock, ShieldCheck,
  Zap, Database, LayoutDashboard, LogIn, Users, PlugZap
} from 'lucide-react';
import loginImg from '../assets/case-invoice/login.jpg';
import dashboardImg from '../assets/case-invoice/dashboard.jpg';
import providersImg from '../assets/case-invoice/providers.jpg';
import integrationImg from '../assets/case-invoice/integration.jpg';

const CaseStudyInvoice = () => {
  const problemSteps = [
    'Open the correct Word (DOCX) invoice template',
    'Log into Redmine or Jira depending on the contractor\u2019s department',
    'Find the contractor, filter by date range and copy each completed task',
    'Translate task descriptions between English and Ukrainian for bilingual invoices',
    'Paste and format every task into the Word document by hand',
    'Add hours per task, calculate totals and write the amount in words (with Ukrainian)',
    'Look up bank details, tax ID and contract data from scattered spreadsheets',
  ];

  const painPoints = [
    'No centralised database of contractors with contract and bank details',
    'Hard to update and maintain contractor information',
    'High risk of copy/paste errors and calculation mistakes',
    'Time-consuming manual translation work',
    'Up to 20 minutes per invoice, with inconsistent formatting',
  ];

  const screens = [
    { icon: <LogIn size={18} />, title: 'Secure Login', description: 'JWT-based authentication with email/password and session management to protect financial and contractor data.', image: loginImg },
    { icon: <LayoutDashboard size={18} />, title: 'Executive Dashboard', description: 'At-a-glance invoicing overview with real-time metrics: total revenue, pending, paid and overdue amounts.', image: dashboardImg },
    { icon: <Database size={18} />, title: 'Contractor Database', description: 'Centralised, searchable contractor records (names, contract dates, bank details, tax IDs) with CSV import/export — replacing scattered spreadsheets. Personal data redacted for this case study.', image: providersImg },
    { icon: <PlugZap size={18} />, title: 'Redmine / Jira Integration', description: 'Select a user (700+ supported) and a period, choose “time logged” or “assigned issues”, then load tasks directly from Redmine or Jira in one click.', image: integrationImg },
  ];

  const stack = [
    ['Frontend', 'React 18, Tailwind CSS, Shadcn UI'],
    ['Backend', 'FastAPI (Python 3.11)'],
    ['Database', 'MongoDB (NoSQL)'],
    ['Authentication', 'JWT-based secure login'],
    ['Task Tracking', 'Redmine REST API, Jira REST API v3 (JQL)'],
    ['Document Generation', 'python-docx (DOCX templating)'],
    ['AI / ML', 'OpenAI GPT — Vision (template parsing) & translation'],
    ['Deployment', 'Netlify (frontend), Render (backend)'],
  ];

  const integrations = [
    ['Redmine API', 'Fetch users, issues, time entries', 'REST API with API-key auth'],
    ['Jira API v3', 'Fetch users and issues via JQL search', 'REST API with Bearer token'],
    ['OpenAI Vision', 'Parse uploaded invoice images to detect fields', 'GPT Vision API'],
    ['OpenAI GPT', 'Translate task descriptions EN\u2194UA', 'GPT Text API'],
  ];

  const impact = [
    ['Time per invoice', '~20 minutes', '~2 minutes', '90% faster'],
    ['Manual data entry', '100%', '0%', 'Eliminated'],
    ['Translation time', '5\u201310 min/invoice', '1 click', 'Instant'],
    ['Contractor database', 'Scattered files', 'Centralised app', 'Single source of truth'],
    ['Error rate', 'High (manual copy)', 'Minimal', 'Significantly reduced'],
    ['Scalability', 'Limited by manual effort', '100+ contractors', '10x capacity'],
  ];

  return (
    <div className="min-h-screen bg-bg-page" data-testid="case-study-invoice">
      {/* Simple top bar */}
      <header className="sticky top-0 z-50 bg-bg-page/90 backdrop-blur-md border-b border-border-medium">
        <div className="container mx-auto px-8 py-4 flex items-center justify-between">
          <Link to="/" className="inline-flex items-center gap-2 nav-link" data-testid="case-back-link">
            <ArrowLeft size={18} />
            Back to portfolio
          </Link>
          <a
            href="/Anna-Krat-CV.pdf"
            download="Anna-Krat-CV.pdf"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-brand-primary text-white text-sm font-medium hover:bg-brand-hover transition-colors"
            data-testid="case-download-cv-btn"
          >
            <Download size={16} />
            CV
          </a>
        </div>
      </header>

      <main className="container mx-auto px-8 py-16 max-w-5xl">
        {/* Title */}
        <div className="mb-12">
          <div className="flex items-center flex-wrap gap-3 mb-5">
            <span className="px-3 py-1 rounded-full text-xs font-medium bg-brand-soft text-brand-primary border border-indigo-100">
              Full-stack • Live • Private company system
            </span>
            <span className="inline-flex items-center gap-1.5 text-xs text-text-muted">
              <Lock size={13} /> Contains personal data — public login not available
            </span>
          </div>
          <h1 className="heading-1 text-text-primary mb-5">Automated Invoice Generation System</h1>
          <p className="body-large text-text-secondary max-w-3xl">
            A full-stack application that automates the entire invoice workflow for a company managing 100+ contractors
            across departments — eliminating manual data entry, centralising contractor management and cutting invoice
            preparation from ~20 minutes to under 2 minutes.
          </p>

          {/* Quick stats */}
          <div className="grid sm:grid-cols-3 gap-6 mt-10">
            {[
              { icon: <Clock className="text-brand-primary" size={22} />, big: '90% faster', small: '20 min → under 2 min per invoice' },
              { icon: <Zap className="text-brand-primary" size={22} />, big: '0% manual entry', small: 'Task copy & translation automated' },
              { icon: <Database className="text-brand-primary" size={22} />, big: '100+ contractors', small: 'Single centralised source of truth' },
            ].map((s, i) => (
              <div key={i} className="team-card p-6" data-testid={`case-stat-${i}`}>
                <div className="mb-3">{s.icon}</div>
                <div className="heading-4 text-text-primary mb-1">{s.big}</div>
                <div className="body-small text-text-secondary">{s.small}</div>
              </div>
            ))}
          </div>
        </div>

        {/* My role */}
        <section className="team-card p-8 mb-16">
          <h2 className="heading-4 text-text-primary mb-3">My role</h2>
          <p className="body-medium">
            Delivered end-to-end, from idea to implementation — workflow design, UX/UI, requirements, full-stack build,
            API integrations and AI features, testing and deployment — built by me with AI agents.
          </p>
        </section>

        {/* The Problem */}
        <section className="mb-16">
          <h2 className="heading-3 text-text-primary mb-8">The problem: a manual invoice process</h2>
          <div className="grid lg:grid-cols-2 gap-10">
            <div>
              <h3 className="heading-6 text-brand-primary uppercase tracking-wide mb-4">Every invoice required</h3>
              <ol className="space-y-3">
                {problemSteps.map((step, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <span className="flex-shrink-0 w-6 h-6 rounded-full bg-brand-soft text-brand-primary text-xs font-semibold flex items-center justify-center">{i + 1}</span>
                    <span className="body-small text-text-primary">{step}</span>
                  </li>
                ))}
              </ol>
            </div>
            <div>
              <h3 className="heading-6 text-brand-primary uppercase tracking-wide mb-4">Pain points</h3>
              <ul className="space-y-3">
                {painPoints.map((p, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-brand-primary rounded-full flex-shrink-0 mt-2" />
                    <span className="body-small text-text-primary">{p}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {/* The Solution - screen gallery */}
        <section className="mb-16">
          <h2 className="heading-3 text-text-primary mb-3">The solution</h2>
          <p className="body-medium mb-8 max-w-3xl">Four key screens from the live application, covering the core workflow from secure login to loading tasks from Redmine/Jira. Client names and other personal data have been redacted.</p>
          <div className="grid md:grid-cols-2 gap-8">
            {screens.map((screen, i) => (
              <div key={i} className="team-card overflow-hidden" data-testid={`case-screen-${i}`}>
                {/* Screenshot */}
                <a
                  href={screen.image}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block aspect-[16/10] bg-bg-light border-b border-border-medium flex items-center justify-center overflow-hidden group"
                >
                  <img
                    src={screen.image}
                    alt={screen.title}
                    className="w-full h-full object-contain transition-transform duration-300 group-hover:scale-[1.02]"
                  />
                </a>
                <div className="p-6">
                  <div className="flex items-center gap-2 mb-2 text-brand-primary">
                    {screen.icon}
                    <h3 className="heading-6 text-text-primary">{i + 1}. {screen.title}</h3>
                  </div>
                  <p className="body-small text-text-secondary">{screen.description}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Tech stack */}
        <section className="mb-16">
          <h2 className="heading-3 text-text-primary mb-8">Technology stack</h2>
          <div className="team-card overflow-hidden">
            {stack.map(([k, v], i) => (
              <div key={i} className={`grid grid-cols-1 sm:grid-cols-3 gap-2 px-6 py-4 ${i !== stack.length - 1 ? 'border-b border-border-light' : ''}`}>
                <span className="caption text-brand-primary">{k}</span>
                <span className="body-small text-text-primary sm:col-span-2">{v}</span>
              </div>
            ))}
          </div>
        </section>

        {/* Integrations */}
        <section className="mb-16">
          <h2 className="heading-3 text-text-primary mb-8">API integrations</h2>
          <div className="grid md:grid-cols-2 gap-6">
            {integrations.map(([name, purpose, method], i) => (
              <div key={i} className="team-card p-6">
                <div className="flex items-center gap-2 mb-2">
                  <PlugZap className="text-brand-primary" size={18} />
                  <h3 className="heading-6 text-text-primary">{name}</h3>
                </div>
                <p className="body-small text-text-primary mb-2">{purpose}</p>
                <p className="caption text-text-secondary">{method}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Business impact */}
        <section className="mb-16">
          <h2 className="heading-3 text-text-primary mb-8">Business impact</h2>
          <div className="team-card overflow-hidden">
            <div className="hidden sm:grid grid-cols-4 gap-4 px-6 py-4 bg-bg-light border-b border-border-medium">
              <span className="caption text-text-secondary">Metric</span>
              <span className="caption text-text-secondary">Before</span>
              <span className="caption text-text-secondary">After</span>
              <span className="caption text-brand-primary">Improvement</span>
            </div>
            {impact.map((row, i) => (
              <div key={i} className={`grid grid-cols-2 sm:grid-cols-4 gap-y-1 gap-x-4 px-6 py-4 ${i !== impact.length - 1 ? 'border-b border-border-light' : ''}`}>
                <span className="body-small text-text-primary font-medium">{row[0]}</span>
                <span className="body-small text-text-secondary">{row[1]}</span>
                <span className="body-small text-text-secondary">{row[2]}</span>
                <span className="body-small text-brand-primary font-medium">{row[3]}</span>
              </div>
            ))}
          </div>
        </section>

        {/* Workflow */}
        <section className="mb-16">
          <h2 className="heading-3 text-text-primary mb-6">User workflow (after implementation)</h2>
          <div className="team-card p-8">
            <div className="flex flex-wrap items-center gap-2">
              {['Login', 'Select invoice type', 'Choose contractor', 'Pick month', 'Fetch tasks (auto)', 'Select tasks', 'Translate (1 click)', 'Generate invoice', 'Download DOCX / PDF'].map((step, i, arr) => (
                <React.Fragment key={i}>
                  <span className="px-3 py-1.5 rounded-full bg-brand-soft text-brand-primary text-sm font-medium border border-indigo-100">{step}</span>
                  {i < arr.length - 1 && <span className="text-text-muted">→</span>}
                </React.Fragment>
              ))}
            </div>
            <div className="flex items-center gap-2 mt-6 text-text-primary">
              <Zap className="text-brand-primary" size={18} />
              <span className="body-medium font-medium">Total time: ~2 minutes</span>
            </div>
          </div>
        </section>

        {/* Highlights */}
        <section className="grid sm:grid-cols-3 gap-6 mb-16">
          {[
            { icon: <ShieldCheck className="text-brand-primary" size={22} />, title: 'Secure by design', text: 'JWT auth and session management protect sensitive financial and contractor data.' },
            { icon: <CheckCircle2 className="text-brand-primary" size={22} />, title: 'Fewer errors', text: 'Automated task import, calculations and translation remove manual copy mistakes.' },
            { icon: <Users className="text-brand-primary" size={22} />, title: 'Scales to teams', text: 'Handles 100+ contractors across multiple departments from one place.' },
          ].map((h, i) => (
            <div key={i} className="team-card p-6">
              <div className="mb-3">{h.icon}</div>
              <h3 className="heading-6 text-text-primary mb-2">{h.title}</h3>
              <p className="body-small text-text-secondary">{h.text}</p>
            </div>
          ))}
        </section>

        {/* Back CTA */}
        <div className="text-center">
          <Link to="/" className="btn-secondary inline-flex items-center gap-2" data-testid="case-back-bottom">
            <ArrowLeft size={18} />
            Back to portfolio
          </Link>
        </div>
      </main>
    </div>
  );
};

export default CaseStudyInvoice;
